import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, UserPlus, Mail, Power, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, downloadPdf } from "@/api";
import { OFFICE, OfficeCard, OfficeButton, OfficeField, OfficeInput } from "@/pages/admin/officeUi";
import { PARTNER_TYPES, partnerTypeLabel, fmtDate, fmtEur } from "@/pages/partner/labels";
import AdminReferrals from "@/pages/admin/AdminReferrals";

const EMPTY = { email: "", agency_name: "", contact_name: "", phone: "", partner_type: "web_agency", start_date: new Date().toISOString().slice(0, 10) };
const selectCls = "w-full rounded-xl border px-4 py-2.5";

function NewPartner({ onCreated }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.adminCreatePartner(f);
      toast.success(r.email_sent ? "Partner creato · invito inviato via email" : "Partner creato (email invito non inviata)");
      setF(EMPTY); onCreated();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <OfficeCard>
      <h2 className="font-display text-lg mb-4">Nuovo partner</h2>
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="new-partner-form">
        <OfficeField label="Nome agenzia *"><OfficeInput required value={f.agency_name} onChange={set("agency_name")} data-testid="partner-agency-input" /></OfficeField>
        <OfficeField label="Referente"><OfficeInput value={f.contact_name} onChange={set("contact_name")} data-testid="partner-contact-input" /></OfficeField>
        <OfficeField label="Email (login) *"><OfficeInput required type="email" value={f.email} onChange={set("email")} data-testid="partner-email-input" /></OfficeField>
        <OfficeField label="Telefono"><OfficeInput value={f.phone} onChange={set("phone")} data-testid="partner-phone-input" /></OfficeField>
        <OfficeField label="Tipologia">
          <select value={f.partner_type} onChange={set("partner_type")} className={selectCls} style={{ borderColor: OFFICE.border }} data-testid="partner-type-select">
            {PARTNER_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </OfficeField>
        <OfficeField label="Inizio collaborazione"><OfficeInput type="date" value={f.start_date} onChange={set("start_date")} data-testid="partner-start-input" /></OfficeField>
        <div className="sm:col-span-2">
          <OfficeButton type="submit" disabled={busy} data-testid="partner-create-btn"><UserPlus className="h-4 w-4" /> {busy ? "Creazione…" : "Crea e invia invito"}</OfficeButton>
        </div>
      </form>
    </OfficeCard>
  );
}

function PartnerRow({ p, onChanged }) {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const t = p.totals || {};
  const toggle = async () => {
    try { const r = await api.adminTogglePartner(p.id); toast.success(r.active ? "Partner riattivato" : "Partner disattivato"); onChanged(); }
    catch (e) { toast.error(e.message); }
  };
  const invite = async () => {
    try { const r = await api.adminResendPartnerInvite(p.id); r.email_sent ? toast.success("Invito reinviato") : toast.error("Invio email non riuscito, riprova più tardi"); } catch (e) { toast.error(e.message); }
  };
  const pdf = async () => {
    const [y, m] = month.split("-");
    try { await downloadPdf(`/admin/partners/${p.id}/statement/${y}/${Number(m)}.pdf`, `estratto-${p.agency_name}-${month}.pdf`); }
    catch (e) { toast.error("PDF non disponibile"); }
  };
  return (
    <OfficeCard className="!p-5" data-testid={`partner-row-${p.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-medium">{p.agency_name} <span className="text-xs ml-1 rounded-full px-2 py-0.5" style={{ background: p.active ? "#E3F4E8" : "#F8E1DC", color: p.active ? "#1F7A3E" : "#8A2E1C" }} data-testid={`partner-active-${p.id}`}>{p.active ? "Attivo" : "Disattivo"}</span></div>
          <div className="text-sm" style={{ color: OFFICE.muted }}>{p.contact_name || "—"} · {p.email} · {p.phone || "—"} · {partnerTypeLabel(p.partner_type)} · dal {fmtDate(p.start_date)}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <OfficeButton variant="ghost" onClick={invite} data-testid={`partner-invite-${p.id}`}><Mail className="h-4 w-4" /> Reinvia invito</OfficeButton>
          <OfficeButton variant={p.active ? "danger" : "ghost"} onClick={toggle} data-testid={`partner-toggle-${p.id}`}><Power className="h-4 w-4" /> {p.active ? "Disattiva" : "Riattiva"}</OfficeButton>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4 text-sm">
        <div>Segnalazioni <b data-testid={`partner-sent-${p.id}`}>{t.sent ?? 0}</b></div>
        <div>Clienti <b data-testid={`partner-clients-${p.id}`}>{t.clients ?? 0}</b></div>
        <div>Maturati <b>{fmtEur(t.accrued)}</b></div>
        <div>Da pagare <b style={{ color: OFFICE.brand }} data-testid={`partner-topay-${p.id}`}>{fmtEur(t.to_pay)}</b></div>
        <div>Pagati <b>{fmtEur(t.paid)}</b></div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-4">
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-xl border px-3 py-2 text-sm" style={{ borderColor: OFFICE.border }} data-testid={`partner-statement-month-${p.id}`} />
        <OfficeButton variant="ghost" onClick={pdf} data-testid={`partner-statement-btn-${p.id}`}><FileDown className="h-4 w-4" /> Genera estratto conto</OfficeButton>
      </div>
    </OfficeCard>
  );
}

export default function AdminPartners() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") === "partners" ? "partners" : "referrals");
  const [partners, setPartners] = useState([]);
  const load = useCallback(async () => {
    try { setPartners(await api.adminPartners()); } catch (e) { toast.error(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const tabBtn = (k, label) => (
    <button onClick={() => setTab(k)} data-testid={`partners-tab-${k}`}
      className="rounded-full px-4 py-2 text-sm font-medium transition-colors"
      style={tab === k ? { background: OFFICE.ink, color: "#fff" } : { color: OFFICE.ink2 }}>{label}</button>
  );

  return (
    <div className="theme-office min-h-screen" style={{ background: OFFICE.surface2, color: OFFICE.ink }} data-testid="admin-partners-page">
      <header className="sticky top-0 z-30" style={{ background: OFFICE.surface, borderBottom: `1px solid ${OFFICE.border}` }}>
        <div className="max-w-6xl mx-auto px-5 md:px-6 h-16 flex items-center gap-3">
          <OfficeButton variant="ghost" onClick={() => navigate("/admin")} data-testid="partners-back-btn"><ArrowLeft className="h-4 w-4" /></OfficeButton>
          <div className="font-display text-lg">Partner & segnalazioni</div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-5 md:px-6 py-6 space-y-5">
        <div className="inline-flex rounded-full p-1 border" style={{ borderColor: OFFICE.border, background: OFFICE.surface }}>
          {tabBtn("referrals", "Segnalazioni")}
          {tabBtn("partners", `Partner (${partners.length})`)}
        </div>
        {tab === "referrals" ? (
          <OfficeButton onClick={() => setTab("partners")} className="ml-3" data-testid="go-add-partner-btn"><UserPlus className="h-4 w-4" /> Aggiungi partner</OfficeButton>
        ) : null}
        {tab === "partners" ? (
          <>
            <NewPartner onCreated={load} />
            {partners.map((p) => <PartnerRow key={p.id} p={p} onChanged={load} />)}
          </>
        ) : <AdminReferrals partners={partners} onChanged={load} />}
      </main>
    </div>
  );
}
